import { isValidAcceptanceList, AcceptanceStaleReason, AzureAcceptance, AzureRowKind } from './acceptance/acceptances';
import { AzureAcceptanceDTO, AzureConfigDTO } from './AzureDTO';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { areDifferent } from 'utils/array';

//
// Enums: the API's numbers (Docs/MS_Azure.md), one evidence shape for the four Azure checks
//
export enum AzureCheck {
  Databases = 0,
  KeyVaults = 1,
  Logs = 2,
  Secrets = 3,
}

export enum AzureUsage {
  InUse = 0,
  NotInUse = 1, //no resource of the check's kind in the readable scope
  NoReadAccess = 2, //the access identity can read no subscription at all
  NoSubscriptionInScope = 3, //every readable subscription is excluded
  NothingChecked = 4, //resources were found, but every row is not counted: none could be read well enough to judge
  ScopeNotValid = 5, //the scope on the connection breaks a rule (too many subscriptions, an exclusion without a reason)
  NotConnected = 6, //the connection has no access identity yet: nothing of the customer's Azure was read
  WaitingForAccess = 7, //the check is on, but the role lacks its reads (the connection needs a role update): nothing was read
}

export enum AzureScopeState {
  InScope = 0,
  Excluded = 1,
  NewInScope = 2,
}

export enum AzureReadState {
  Read = 0,
  NotReadable = 1,
  NotEnabled = 2,
}

export enum AzureResourceType {
  SqlServer = 0,
  PostgreSqlFlexible = 1,
  MySqlFlexible = 2,
  KeyVault = 3,
  AppService = 4,
  Subscription = 5,
  KeyVaultKey = 6,
  KeyVaultSecret = 7,
}

//The fact on a row; the API decides the kind it gets.
export enum AzureState {
  Ok = 0,
  //databases
  OpenToInternet = 10,
  WideRange = 11,
  OpenToAzure = 12,
  PrivateOnly = 13,
  //Key Vaults
  AccessPolicies = 20,
  NoPurgeProtection = 21,
  AccessPoliciesAndNoPurgeProtection = 22,
  //logs
  LogOff = 30,
  RetentionTooShort = 31,
  RetentionUnknown = 32, //the destination does not tell how long it keeps the log: confirm manually
  DestinationNotReadable = 33,
  ActivityLogNotVisible = 34,
  //secrets and keys
  NoEndDate = 40,
  ExpiresSoon = 41,
  Expired = 42,
  KeysNotReadable = 43,
  //any check
  SubscriptionNotReadable = 90,
}

export enum AzureLogSource {
  SqlAuditing = 0,
  AppServiceHttpLogs = 1,
  ActivityLog = 2,
}

export enum AzureLogDestination {
  None = 0,
  LogAnalytics = 1,
  Storage = 2,
  EventHub = 3,
}

//The states that mean the app could not see enough to judge: findings, shown as "could not check".
export const isAzureCouldNotCheckState = (state: AzureState): boolean => {
  return (
    state === AzureState.SubscriptionNotReadable ||
    state === AzureState.DestinationNotReadable ||
    state === AzureState.ActivityLogNotVisible
  );
};

//A finding the user can accept until a date. An unreadable subscription cannot be accepted (the API ignores such an
//acceptance): the fix is to give the Reader role back or to exclude the subscription from the scope.
export const isAzureAcceptableState = (state: AzureState): boolean => state !== AzureState.SubscriptionNotReadable;

//
// Config: dated acceptances per resource and the one option of the check
//
//An acceptance in a stored config or in the evidence (no longer applies). Its dates stay the API's strings: a Save
//sends the config back as it is.
export const mapFromAzureAcceptance = (dto: AzureAcceptanceDTO): AzureAcceptance => {
  const acceptance = new AzureAcceptance();
  acceptance.resourceId = dto.resourceId ?? '';
  acceptance.resourceName = dto.resourceName ?? '';
  acceptance.until = dto.until ?? '';
  acceptance.reason = dto.reason ?? '';
  acceptance.acceptedAtUtc = dto.acceptedAtUtc ?? '';
  acceptance.state = dto.state ?? undefined;

  return acceptance;
};

const mapFromAzureAcceptances = (stored: AzureConfigDTO | undefined): AzureAcceptance[] =>
  (stored?.accepted ?? []).map(mapFromAzureAcceptance);

const isSameAcceptance = (x: AzureAcceptance, y: AzureAcceptance): boolean =>
  x.resourceId.toLowerCase() === y.resourceId.toLowerCase() &&
  x.until === y.until &&
  x.reason === y.reason &&
  x.acceptedAtUtc === y.acceptedAtUtc &&
  x.state === y.state;

//An acceptance key is the ARM id of a resource, which always starts with its subscription.
export const isValidAzureResourceId = (id: string): boolean => id.toLowerCase().startsWith('/subscriptions/');

export class AzureConfigBase extends IntegrationConfigBase {
  accepted: AzureAcceptance[] = [];

  isEqual(other: AzureConfigBase): boolean {
    return !areDifferent(this.accepted, other.accepted, isSameAcceptance);
  }

  isValid(): boolean {
    return isValidAcceptanceList(this.accepted, isValidAzureResourceId);
  }

  clone(): AzureConfigBase {
    const clone = new AzureConfigBase();
    clone.accepted = [...this.accepted];

    return clone;
  }
}

export class IntegrationConfig_MS_AzureDatabases extends AzureConfigBase {
  allowAzureServicesIsFinding: boolean = false; //"Open to all of Azure" is a finding instead of a warning

  static fromStored(stored: AzureConfigDTO | undefined): IntegrationConfig_MS_AzureDatabases {
    const config = new IntegrationConfig_MS_AzureDatabases();
    config.accepted = mapFromAzureAcceptances(stored);
    config.allowAzureServicesIsFinding = stored?.allowAzureServicesIsFinding ?? false;

    return config;
  }

  isEqual(other: IntegrationConfig_MS_AzureDatabases): boolean {
    return super.isEqual(other) && this.allowAzureServicesIsFinding === other.allowAzureServicesIsFinding;
  }

  clone(): IntegrationConfig_MS_AzureDatabases {
    const clone = new IntegrationConfig_MS_AzureDatabases();
    clone.accepted = [...this.accepted];
    clone.allowAzureServicesIsFinding = this.allowAzureServicesIsFinding;

    return clone;
  }
}

export class IntegrationConfig_MS_AzureKeyVaults extends AzureConfigBase {
  static fromStored(stored: AzureConfigDTO | undefined): IntegrationConfig_MS_AzureKeyVaults {
    const config = new IntegrationConfig_MS_AzureKeyVaults();
    config.accepted = mapFromAzureAcceptances(stored);

    return config;
  }

  clone(): IntegrationConfig_MS_AzureKeyVaults {
    const clone = new IntegrationConfig_MS_AzureKeyVaults();
    clone.accepted = [...this.accepted];

    return clone;
  }
}

export const MinRetentionDays = 1;
export const MaxRetentionDays = 4383;
export const DefaultRetentionDays = 90;

export class IntegrationConfig_MS_AzureLogs extends AzureConfigBase {
  minimumRetentionDays: number = DefaultRetentionDays; //exactly the minimum passes

  static fromStored(stored: AzureConfigDTO | undefined): IntegrationConfig_MS_AzureLogs {
    const config = new IntegrationConfig_MS_AzureLogs();
    config.accepted = mapFromAzureAcceptances(stored);
    config.minimumRetentionDays = stored?.minimumRetentionDays ?? DefaultRetentionDays;

    return config;
  }

  isEqual(other: IntegrationConfig_MS_AzureLogs): boolean {
    return super.isEqual(other) && this.minimumRetentionDays === other.minimumRetentionDays;
  }

  isValid(): boolean {
    return (
      super.isValid() &&
      Number.isInteger(this.minimumRetentionDays) &&
      this.minimumRetentionDays >= MinRetentionDays &&
      this.minimumRetentionDays <= MaxRetentionDays
    );
  }

  clone(): IntegrationConfig_MS_AzureLogs {
    const clone = new IntegrationConfig_MS_AzureLogs();
    clone.accepted = [...this.accepted];
    clone.minimumRetentionDays = this.minimumRetentionDays;

    return clone;
  }
}

export const MinWarnDays = 1;
export const MaxWarnDays = 365;
export const DefaultWarnDays = 30;

export class IntegrationConfig_MS_AzureSecrets extends AzureConfigBase {
  warnDays: number = DefaultWarnDays;

  static fromStored(stored: AzureConfigDTO | undefined): IntegrationConfig_MS_AzureSecrets {
    const config = new IntegrationConfig_MS_AzureSecrets();
    config.accepted = mapFromAzureAcceptances(stored);
    config.warnDays = stored?.warnDays ?? DefaultWarnDays;

    return config;
  }

  isEqual(other: IntegrationConfig_MS_AzureSecrets): boolean {
    return super.isEqual(other) && this.warnDays === other.warnDays;
  }

  isValid(): boolean {
    return (
      super.isValid() && Number.isInteger(this.warnDays) && this.warnDays >= MinWarnDays && this.warnDays <= MaxWarnDays
    );
  }

  clone(): IntegrationConfig_MS_AzureSecrets {
    const clone = new IntegrationConfig_MS_AzureSecrets();
    clone.accepted = [...this.accepted];
    clone.warnDays = this.warnDays;

    return clone;
  }
}

//
// Evidence rows
//
export class AzureFirewallRule {
  name: string = '';

  startIp: string = '';

  endIp: string = '';

  addresses: number = 0;
}

export class AzureDatabaseDetail {
  publicNetworkAccess?: string; //Enabled or Disabled

  entraOnlyAuth?: boolean; //undefined for MySQL

  ruleCount: number = 0;

  privateEndpoints: number = 0;

  allowAzureServices: boolean = false;

  rules: AzureFirewallRule[] = []; //the rules that decided the state, at most 20
}

export class AzureVaultDetail {
  rbacAuthorization?: boolean;

  purgeProtection?: boolean;

  softDelete?: boolean;

  softDeleteRetentionDays?: number;

  publicNetworkAccess?: string;
}

export class AzureLogDetail {
  source: AzureLogSource = AzureLogSource.SqlAuditing;

  destination: AzureLogDestination = AzureLogDestination.None;

  enabled: boolean = false;

  retentionDays?: number; //0 = unlimited, undefined = could not read

  retentionSource: string = ''; //table:<name>, storage, auditing or empty

  categories: string[] = [];

  workspaceId?: string;
}

export class AzureSecretDetail {
  itemType: AzureResourceType = AzureResourceType.KeyVaultSecret;

  vaultName: string = '';

  enabled: boolean = false;

  expiresUtc?: Date;

  createdUtc?: Date;

  daysLeft?: number;
}

export class AzureRow {
  resourceId: string = ''; //the ARM id: the portal link and the acceptance key

  resourceName: string = '';

  resourceGroup: string = '';

  subscriptionId: string = '';

  resourceType: AzureResourceType = AzureResourceType.Subscription;

  state: AzureState = AzureState.Ok;

  kind: AzureRowKind = AzureRowKind.Ok;

  found: string = ''; //a raw value, never a sentence

  expected: string = '';

  acceptedUntil?: Date; //the acceptance's last day, a calendar day

  acceptedReason?: string;

  acceptedBy?: string; //the e-mail of who saved the acceptance

  acceptedAtUtc?: Date;

  database?: AzureDatabaseDetail;

  vault?: AzureVaultDetail;

  log?: AzureLogDetail;

  secret?: AzureSecretDetail;
}

export class AzureCounts {
  subscriptionsInScope: number = 0;

  subscriptionsRead: number = 0;

  subscriptionsNotReadable: number = 0;

  subscriptionsNew: number = 0;

  subscriptionsExcluded: number = 0;

  resourceGroupsExcluded: number = 0; //in the subscriptions in scope

  resources: number = 0;

  ok: number = 0;

  findings: number = 0;

  accepted: number = 0;

  explained: number = 0;

  notCounted: number = 0;

  openToAzure: number = 0;

  entraOnly: number = 0;

  retentionUnknown: number = 0;

  keys: number = 0;

  secrets: number = 0;
}

export class AzureSubscriptionRow {
  subscriptionId: string = '';

  displayName: string = '';

  scopeState: AzureScopeState = AzureScopeState.InScope;

  readState: AzureReadState = AzureReadState.Read;

  resources: number = 0;

  excludedResourceGroups: string[] = []; //outside the ISMS scope: not read, not judged
}

export class AzureStaleAcceptance {
  acceptance: AzureAcceptance = new AzureAcceptance();

  why: AcceptanceStaleReason = AcceptanceStaleReason.Expired;
}

//What a test run of an Azure check found; the evidence class (IntegrationConfig_MS_Azure) holds it, the texts read it.
export interface IAzureResult {
  evaluatedAtUtc: Date;
  check: AzureCheck;
  usage: AzureUsage;
  pass: boolean;
  azureTenantId: string;
  minimumRetentionDays?: number; //logs: the minimum in force during the run
  warnDays?: number; //secrets: the warning period in force during the run
  allowAzureServicesIsFinding: boolean; //databases: the option in force during the run
  counts: AzureCounts;
  subscriptions: AzureSubscriptionRow[];
  rows: AzureRow[];
}

export const getAzureSubscriptionName = (result: IAzureResult, subscriptionId: string): string => {
  const subscription = result.subscriptions.find(
    (s) => s.subscriptionId.toLowerCase() === subscriptionId.toLowerCase(),
  );

  return subscription?.displayName || subscriptionId;
};

//The name an acceptance keeps: a vault item is named with its vault, as Azure shows it.
export const getAzureRowName = (row: AzureRow): string => {
  if (row.secret?.vaultName) return `${row.secret.vaultName}/${row.resourceName}`;

  return row.resourceName;
};

//The stored config of a check as its class, with every default filled.
export const getAzureConfigFromStored = (check: AzureCheck, stored: AzureConfigDTO | undefined): AzureConfigBase => {
  switch (check) {
    case AzureCheck.Databases:
      return IntegrationConfig_MS_AzureDatabases.fromStored(stored);
    case AzureCheck.Logs:
      return IntegrationConfig_MS_AzureLogs.fromStored(stored);
    case AzureCheck.Secrets:
      return IntegrationConfig_MS_AzureSecrets.fromStored(stored);
    default:
      return IntegrationConfig_MS_AzureKeyVaults.fromStored(stored);
  }
};

//Key Vaults is the one check without an option.
export const hasAzureOption = (check: AzureCheck): boolean => check !== AzureCheck.KeyVaults;

//Whether the option in a working config differs from the one the shown run used.
export const isAzureOptionChanged = (check: AzureCheck, config: AzureConfigBase, result: IAzureResult): boolean => {
  switch (check) {
    case AzureCheck.Databases:
      return (
        (config as IntegrationConfig_MS_AzureDatabases).allowAzureServicesIsFinding !==
        result.allowAzureServicesIsFinding
      );
    case AzureCheck.Logs:
      return (
        (config as IntegrationConfig_MS_AzureLogs).minimumRetentionDays !==
        (result.minimumRetentionDays ?? DefaultRetentionDays)
      );
    case AzureCheck.Secrets:
      return (config as IntegrationConfig_MS_AzureSecrets).warnDays !== (result.warnDays ?? DefaultWarnDays);
    default:
      return false;
  }
};

//One count behind a usage message (subscriptions in scope, read, excluded, not readable).
export interface IUsageFact {
  label: string;
  value: string | number;
}

//Links into the Azure portal. The portal opens in the customer's directory when the evidence names its tenant;
//without it, in the directory the user signed in to last. The browse paths are the portal's own (not an API), so a
//path that stops working only changes where the link lands.
const portalBase = 'https://portal.azure.com/';

const getPortalRoot = (azureTenantId: string | undefined): string => {
  return azureTenantId ? `${portalBase}#@${azureTenantId}` : `${portalBase}#`;
};

//The portal page of one resource: a server, a vault, a web app, a vault item or a subscription.
export const getAzurePortalResourceUrl = (resourceId: string, azureTenantId: string | undefined): string => {
  return `${getPortalRoot(azureTenantId)}/resource${resourceId}`;
};

//The portal page that fixes a row: Access control (IAM) of the subscription when the app lacks the Reader role on
//it, otherwise the resource itself.
export const getAzurePortalRowUrl = (row: AzureRow, azureTenantId: string | undefined): string => {
  if (row.state === AzureState.SubscriptionNotReadable || row.state === AzureState.ActivityLogNotVisible) {
    return `${getPortalRoot(azureTenantId)}/resource/subscriptions/${row.subscriptionId}/users`;
  }

  return getAzurePortalResourceUrl(row.resourceId, azureTenantId);
};

export const getAzurePortalSubscriptionsUrl = (azureTenantId: string | undefined): string => {
  return `${getPortalRoot(azureTenantId)}/view/Microsoft_Azure_Billing/SubscriptionsBladeV2`;
};

//The portal list of the resources one check reads.
export const getAzurePortalCheckUrl = (check: AzureCheck, azureTenantId: string | undefined): string => {
  switch (check) {
    case AzureCheck.Databases:
      return `${getPortalRoot(azureTenantId)}/browse/Microsoft.Sql%2Fservers`;
    case AzureCheck.KeyVaults:
    case AzureCheck.Secrets:
      return `${getPortalRoot(azureTenantId)}/browse/Microsoft.KeyVault%2Fvaults`;
    case AzureCheck.Logs:
      return `${getPortalRoot(
        azureTenantId,
      )}/view/Microsoft_Azure_Monitoring/AzureMonitoringBrowseBlade/~/diagnosticsLogs`;
    default:
      return getAzurePortalSubscriptionsUrl(azureTenantId);
  }
};

//The evidence row with any acceptance added or removed in the current session.
export interface IAzureViewRow {
  id: string;
  row: AzureRow;
  kind: AzureRowKind;
  acceptance?: AzureAcceptance;
  subscriptionName: string;
}
